import type { ReactNode } from 'react';
import { useSubscription, type FeatureGate } from '@/contexts/SubscriptionContext';
import { UpgradePrompt } from './UpgradePrompt';

interface SubscriptionGateProps {
  feature: FeatureGate;
  children: ReactNode;
  fallback?: ReactNode;
}

/**
 * The Velvet Rope - Subscription middleware component
 * Wraps premium features and shows upgrade prompt if user doesn't have access
 */
export function SubscriptionGate({ feature, children, fallback }: SubscriptionGateProps) {
  const { canAccessFeature, loading, isActive } = useSubscription();

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="h-6 w-6 animate-spin rounded-full border-2 border-cacao-dark-tertiary border-t-cacao-gold" />
      </div>
    );
  }

  if (!isActive) {
    return fallback ?? <UpgradePrompt reason="subscription_expired" feature={feature} />;
  }

  if (!canAccessFeature(feature)) {
    return fallback ?? <UpgradePrompt reason="feature_locked" feature={feature} />;
  }

  return <>{children}</>;
}
